import type { IClient } from "../Client.js";
import type { DataHandler } from "../DataHandler.js";
import { EventTarget } from "../events.js";
import { UploadTask, type UploadTaskOptions } from "./UploadTask.js";

export type UploadManagerEventToValueMap = {
	onProgress: {
		loaded: number;
		total: number;
	};
};

export type UploadManagerEvent = keyof UploadManagerEventToValueMap;

export const UploadManagerEvents: Record<UploadManagerEvent, { abortable: boolean }> = {
	onProgress: { abortable: false },
};

export type UploadManagerOptions = {
	client?: IClient | null;
	dataHandler?: DataHandler<any>;
	url?: string;
	fieldName?: string;
	primKey?: string;
	concurrency?: number;
	/**
	 * Fields sent with every upload, read when the upload is queued. Fields passed to
	 * `upload` take precedence.
	 */
	defaultFields?: () => Record<string, any>;
};

export class UploadManager extends EventTarget<UploadManagerEventToValueMap> {
	private options: UploadManagerOptions;
	private tasks: UploadTask[];
	private concurrency: number;

	constructor(options: UploadManagerOptions) {
		super(UploadManagerEvents);
		this.options = options;
		this.tasks = [];
		this.concurrency = options.concurrency ?? 3;
	}

	private startNext() {
		let active = this.tasks.filter((t) => t.getStatus() === "uploading").length;
		for (let task of this.tasks) {
			if (active >= this.concurrency) {
				break;
			}
			if (task.getStatus() === "pending") {
				active++;
				task
					.start()
					.catch(() => {})
					.finally(() => {
						this.startNext();
					});
			}
		}
	}

	private updateProgress() {
		let loaded = 0;
		let total = 0;
		for (let task of this.tasks) {
			let progress = task.getProgress();
			loaded += progress.loaded;
			total += progress.total;
		}
		this.fireEvent("onProgress", { loaded, total });
	}

	protected createTask(options: UploadTaskOptions) {
		return new UploadTask(options);
	}

	upload(file: File | Blob, fields?: Record<string, any>, fileName?: string) {
		const { defaultFields, ...options } = this.options;
		const taskOptions: UploadTaskOptions = {
			...options,
			file,
			fields: { ...defaultFields?.(), ...fields },
			fileName,
		} as UploadTaskOptions;
		const task = this.createTask(taskOptions);
		task.attachEvent("onProgress", () => this.updateProgress());
		task.attachEvent("onStatus", () => {
			this.updateProgress();
			if (task.getStatus() !== "uploading" && task.getStatus() !== "pending") {
				this.startNext();
			}
		});
		this.tasks.push(task);
		this.updateProgress();
		this.startNext();
		return task;
	}
}
